/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import voca from 'voca';

import { useLocales } from 'src/locales';
import {
  AUTH_STORAGE_KEYS,
  localStorageGetItem,
  localStorageSetItem,
} from 'src/utils/storage-available';
import { allLangs, defaultLang } from 'src/locales/locales-config';
import { fCurrency, fCurrencyAfterSymbol, fPercent } from 'src/utils/format-number';
import { fConvertDateFormat } from 'src/utils/format-date';
import NativesLogo from 'src/assets/images/Logo_chisiamo.png';
import planeIcon from 'src/assets/images/plane.png';
import NativesLogoFooter from 'src/assets/images/Logo_page_footer.png';
import NativesWhiteLogoFooter from 'src/assets/images/Logo_page_footer_white.png';
import fair from 'src/assets/images/fair.jpg';
import WalletLogo from 'src/assets/images/wallet.png';
import NativesLogoOriginal from 'src/assets/images/NativesLogoOriginal.png';
import DiscountsLogo from 'src/assets/images/discount.png';
import DeliveryLogo from 'src/assets/images/deliver.png';
import PackageLogo from 'src/assets/images/box-linear.png';
import { IOverlayItem, IProductItem } from 'src/types';
import 'src/theme/product-pdf-styles.css';
import { FooterHtmlProps, Page2InfoType } from 'src/types/product-pdf';
import { useGetClient, useGetClientDiscount } from 'src/api/admin';
import useGetData from 'src/hooks/use-get-data';
import { endpoints } from 'src/utils/axios';
import { decodePayload } from 'src/utils/misc';
import { ANTICIPATED_PAYMENT_DURATION_LABELS, PAYMENT_DURATION_OPTIONS } from 'src/utils/xp-utils';
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

// Page layout constants (A4 = 297mm ≈ 1122px at 96dpi)
// All heights in px to match the 210mm-wide / 297mm-tall page dimensions
const PAGE_TOTAL_HEIGHT = 1122; // 297mm in px
const PAGE_TOP_PADDING = 57; // 1.5cm top padding
const PAGE_FOOTER_HEIGHT = 200; // footer: content (~40px) + 1.5cm bottom padding (~57px) + generous safety margin
const PAGE_USABLE_HEIGHT = PAGE_TOTAL_HEIGHT - PAGE_TOP_PADDING - PAGE_FOOTER_HEIGHT; // ~865px

// Index page height estimates
const INDEX_MAIN_TITLE_HEIGHT = 80; // "INDICE" h1 (53px font + margin)
const INDEX_SUB_TITLE_HEIGHT = 45; // section subtitle (26px font + margin)
const INDEX_COUNTRY_HEADER_HEIGHT = 45; // country name bar (18px font + padding)
const INDEX_PRODUCER_ROW_HEIGHT = 32; // each producer row in 2-col layout (name + origin + margin)
const INDEX_COUNTRY_MARGIN = 20; // margin-bottom on country section

// Product listing height estimates
const PRODUCT_COUNTRY_HEADER_HEIGHT = 65; // country header bar + margin
const PRODUCT_PRODUCER_HEADER_HEIGHT = 85; // producer name (29px) + region (13px) + margins + spacing
const PRODUCT_TABLE_HEADER_HEIGHT = 35; // table column headers + bottom border + padding
const PRODUCT_ROW_HEIGHT = 52; // each product row (padding + multi-line wine names)
const PRODUCT_PRODUCER_MARGIN = 28; // margin between producer blocks

const NON_WINE_TYPE_keys = [
  'analcolico',
  'succhi_di_frutta',
  'kombucha',
  'birra',
  'birre_acide',
  'sidro',
  'distillati',
  'enolito',
  'vermouth',
  'altri',
  'caffè',
  'olio',
];

// Delivery pricing constants
const DELIVERY_PRICING = {
  MILANO: 360,
  REMOTE_AREAS: 480,
  LABELS: {
    MILANO: 'Milano',
    REMOTE_AREAS: 'product_pdf_gen_rules_3_pt_1_info_remote_areas',
  },
} as const;

// Utility function to format minimum order information
function formatMinimumOrderInfo(minimumOrderValue: number, t: (key: string) => string): string {
  const { MILANO, REMOTE_AREAS, LABELS } = DELIVERY_PRICING;
  const currencySymbol = fCurrency(0).replace(/[\d.,\s]/g, '');
  return [
    fCurrency(minimumOrderValue),
    `${currencySymbol}${MILANO} ${LABELS.MILANO}`,
    `${currencySymbol}${REMOTE_AREAS} ${t(LABELS.REMOTE_AREAS)}`,
  ].join('\n');
}
// Slugify function for safe HTML id and href anchors
function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

const PAGE_2_INFO_1 = [
  {
    label: 'product_pdf_gen_rules_1_pt_1_label',
    info: 'product_pdf_gen_rules_1_pt_1_info',
    dynamic: false,
  },
  {
    label: 'product_pdf_gen_rules_1_pt_2_label',
    info: 'product_pdf_gen_rules_1_pt_2_info',
    dynamic: true,
  },
];

const PAGE_2_INFO_2 = [
  { label: 'product_pdf_gen_rules_2_pt_1_label', info: 'product_pdf_gen_rules_2_pt_1_info' },
  { label: 'product_pdf_gen_rules_2_pt_2_label', info: 'product_pdf_gen_rules_2_pt_2_info' },
  {
    label: 'product_pdf_gen_rules_2_pt_3_label',
    info: 'product_pdf_gen_rules_2_pt_3_info',
  },
  {
    label: 'product_pdf_gen_rules_2_pt_4_label',
    info: 'product_pdf_gen_rules_2_pt_4_info',
  },
];

const PAGE_2_INFO_2_WITH_DISCOUNT: Page2InfoType[] = [
  {
    label: 'product_pdf_gen_rules_2_pt_1_label_discount',
    info: 'product_pdf_gen_rules_2_pt_1_info_discount',
    subInfo: 'product_pdf_gen_rules_2_pt_1_info_subInfo',
    dynamicInfo: {
      info1: 'product_pdf_gen_rules_2_pt_1_info_dynamicInfo_1',
      info3: 'product_pdf_gen_rules_2_pt_1_info_dynamicInfo_3',
      info4: 'product_pdf_gen_rules_2_pt_1_info_dynamicInfo_4',
    },
    dynamic: true,
  },
  {
    label: 'product_pdf_gen_rules_2_pt_1_label',
    info: 'product_pdf_gen_rules_2_pt_1_info',
    dynamic: false,
  },
];

const PAGE_2_INFO_3 = [
  { label: 'product_pdf_gen_rules_3_pt_1_label', info: 'product_pdf_gen_rules_3_pt_1_info' },
  { label: 'product_pdf_gen_rules_3_pt_2_label', info: 'product_pdf_gen_rules_3_pt_2_info' },
  { label: 'product_pdf_gen_rules_3_pt_3_label', info: 'product_pdf_gen_rules_3_pt_3_info' },
];

const PAGE_2_INFO_4 = [
  { label: 'product_pdf_gen_rules_4_pt_1_label', info: 'product_pdf_gen_rules_4_pt_1_info' },
  { label: 'product_pdf_gen_rules_4_pt_2_label', info: 'product_pdf_gen_rules_4_pt_2_info' },
];

// ----------------------------------------------------------------------

const Footer = ({
  useWhiteLogo,
  footerSeparatorColor = '#ffffff',
  currentPage = 1,
}: FooterHtmlProps) => {
  const { t } = useLocales('productsPage');

  const todaysDate = fConvertDateFormat(new Date().toISOString());

  return (
    <div className={`pdf-footer ${useWhiteLogo ? 'pdf-footer-white' : ''}`}>
      <div className="pdf-footer-copyright-text">
        <span className="pdf-details">
          {t('product_pdf_footer_text')} <span className="pdf-details">{todaysDate}</span>
        </span>
      </div>

      <div className="pdf-divider" style={{ borderBottom: `1px solid ${footerSeparatorColor}` }} />

      <div className="pdf-footer-right-section">
        <img
          src={useWhiteLogo ? NativesWhiteLogoFooter : NativesLogoFooter}
          className="pdf-footer-logo"
          alt="Natives Logo"
        />
        <div className="pdf-footer-page-number">
          <span className="pdf-details">{currentPage}</span>
        </div>
      </div>
    </div>
  );
};

const LabelAndInfo = ({ label = '', info = '' }: { label?: string; info?: string }) => {
  const { t } = useLocales();
  const translatedInfo = t(info);
  const isMultiLine = translatedInfo.includes('\n');

  return (
    <>
      <div className="pdf-label-and-info-container">
        <p className="pdf-label-and-info-label">{t(label)}</p>
        {isMultiLine ? (
          <div className="pdf-label-and-info-info">
            {translatedInfo.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        ) : (
          <span className="pdf-label-and-info-info">{translatedInfo}</span>
        )}
      </div>
      <br />
    </>
  );
};

const SubLabel = ({ label = '', logo }: { label?: string; logo?: string }) => {
  const { t } = useLocales('productsPage');

  return (
    <div className="pdf-sub-label-container">
      <img src={logo || NativesLogo} className="pdf-sub-label-logo" alt="Sublabel Logo" />
      <span className="pdf-sub-label-text">{t(label)}</span>
    </div>
  );
};
type WineType = keyof typeof wineTypeColors;

const wineTypeColors = {
  bianco: '#E1CB7B',
  birra: '#870505',
  macerato: '#F26133',
  rifermentato_bianco: '#C2E3E3',
  rosso: '#870505',
  rosato: '#FC88AB',
  metodo_classico: '#e6d378',
  metodo_classico_rosato: '#dc7464',
  metodo_classico_rosso: '#870505',
  rifermentato_rosato: '#e4796a',
  rifermentato_rosso: '#870505',
  distillati: '#e8d47c',
  analcolico: '#080404',
  caffè: '#080404',
  sidro: '#e6d378',
  olio: '#e7d98e',
  vermouth: '#080404',
  enolito: '#080404',
  passito: '#080404',
  kombucha: '#080404',
  succhi_di_frutta: '#080404',
  birre_acide: '#080404',
  altri: '#080404',
  default: '#080404',
};

const NameCell = ({ label = '', info = '' }: { label?: string; info?: string }) => (
  <div className="pdf-name-cell">
    <div className="pdf-name-cell-icon-wrapper">
      <div
        className="pdf-name-cell-icon"
        style={{
          backgroundColor: info ? wineTypeColors[info as WineType] : wineTypeColors.default,
        }}
      />
    </div>
    <div className="pdf-name-cell-text-wrapper">
      <span className="pdf-product-name">{label}</span>
      <span className="pdf-product-wine-type">
        {voca.capitalize(voca.replaceAll(info, '_', ' '))}
      </span>
    </div>
  </div>
);

// Helper to get availability bar CSS class from quantity
const getAvailabilityClass = (quantity: number): string => {
  if (quantity <= 12) return 'pdf-availability-h25';
  if (quantity <= 30) return 'pdf-availability-h50';
  if (quantity <= 60) return 'pdf-availability-h75';
  return 'pdf-availability-h100';
};

// --- Main ProductPDFPreviewHTML Component ---
export default function ProductPDFPreviewHTML() {
  const { t } = useLocales('productsPage');
  // Ref for the paged content - keeping for potential future use
  const pagedContainerRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();
  const encoded = searchParams.get('data');
  const parsedData: any = decodePayload(encoded) ?? {};

  // Generic render: Doppio visits /pdf/listino-aggiornato with no ?data= param.
  // In this case we must not call admin-protected endpoints.
  const isGenericRender = !encoded;

  const langStorage = localStorageGetItem('i18nextLng');

  localStorageSetItem(AUTH_STORAGE_KEYS.accessToken, parsedData?.pdfToken || '');

  // Client-specific PDF (authenticated via pdfToken)
  const { data: adminProductData } = useGetData({
    endpoint: !isGenericRender ? endpoints.admin.products.root : null,
    params: !isGenericRender
      ? {
          slug: parsedData.client,
          pdfToken: parsedData.pdfToken,
          ...parsedData,
          all: true,
          forPdf: true,
          shown: 'yes',
        }
      : undefined,
  });
  // Generic listino — public endpoint, no auth required
  const { data: publicProductData } = useGetData({
    endpoint: isGenericRender ? endpoints.public.generic : null,
    params: isGenericRender ? { all: true, forPdf: true, shown: 'yes' } : undefined,
  });
  const productApiData: any = isGenericRender ? publicProductData : adminProductData;

  const { data: clientData } = useGetClient({
    slug: parsedData.client,
    pdfToken: parsedData.pdfToken,
    ...parsedData,
  });
  const { data: discountRecord } = useGetClientDiscount({
    slug: parsedData.client,
    pdfToken: parsedData.pdfToken,
  });
  // Overlay is only available for authenticated (client-specific) renders
  const { data: activeOverlay } = useGetData<unknown, IOverlayItem>({
    endpoint: !isGenericRender ? endpoints.admin.overlay.activePDF : null,
  });

  // Only treat the overlay as active if it is enabled AND the current date is within its date range
  const validActiveOverlay = useMemo(() => {
    if (!activeOverlay || !activeOverlay.active) return null;

    const now = new Date();

    if (activeOverlay.startDate) {
      const start = new Date(activeOverlay.startDate);
      // Guard against invalid startDate values
      if (Number.isNaN(start.getTime())) {
        return null;
      }
      if (now < start) {
        return null;
      }
    }

    if (activeOverlay.endDate) {
      const end = new Date(activeOverlay.endDate);
      // Guard against invalid endDate values
      if (Number.isNaN(end.getTime())) {
        return null;
      }
      // Normalize endDate to the end of that day so the overlay
      // remains valid for the full calendar day
      end.setHours(23, 59, 59, 999);
      if (now > end) {
        return null;
      }
    }
    return activeOverlay;
  }, [activeOverlay]);

  // Paged.js integration - Removed for doppio.sh compatibility
  // doppio.sh will handle the PDF rendering
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Only add dependencies to re-render when data changes
      console.log('PDF content ready for rendering');
    }
  }, [productApiData, clientData, discountRecord, validActiveOverlay]);

  let discount = null;

  let paymentDuration = PAYMENT_DURATION_OPTIONS[0].label;
  const currentFrontendURL = window.location.origin;

  let latestDownloadLink = `${currentFrontendURL}${paths.public.downloadProducts(parsedData.pdfToken)}&data=${encoded}`;

  if (clientData?.slug) {
    const crs =
      PAYMENT_DURATION_OPTIONS.find((pdo) => pdo.value === clientData.payment_duration) ||
      PAYMENT_DURATION_OPTIONS[0];

    paymentDuration =
      clientData.payment_duration === ANTICIPATED_PAYMENT_DURATION_LABELS.value
        ? crs.pdfLabel || ANTICIPATED_PAYMENT_DURATION_LABELS.pdfLabel
        : crs.label || PAYMENT_DURATION_OPTIONS[0].label;

    if (discountRecord?.discounts) {
      discount = Object.values(discountRecord?.discounts)[0];
    }

    latestDownloadLink = `${currentFrontendURL}${paths.public.downloadProducts(parsedData.pdfToken)}?clientSlug=${clientData.slug}&pdfToken=${parsedData.pdfToken}&data=${encoded}`;
  }

  const page2Info3 = useMemo(() => {
    const clientMinimumOrderValue = clientData?.minimum_order_total_end_price;
    if (clientMinimumOrderValue) {
      return PAGE_2_INFO_3.map((item) => {
        if (item.info === 'product_pdf_gen_rules_3_pt_1_info') {
          return {
            ...item,
            info: formatMinimumOrderInfo(clientMinimumOrderValue, t),
          };
        }
        return item;
      });
    }
    return PAGE_2_INFO_3;
  }, [clientData?.minimum_order_total_end_price, t]);

  const customerName = useMemo(() => clientData?.name, [clientData]);
  const todaysDate = fConvertDateFormat(new Date().toISOString());
  const currentLang = allLangs.find((lang) => lang.value === langStorage) || defaultLang;

  const abtPageDesc =
    currentLang.value !== defaultLang.value ? (
      <div className="pdf-about-page-description-text">
        <p>
          The distribution was born at the end of 2014, starting from a <strong>passion</strong>{' '}
          that had led us to discover <strong>people</strong>, territories, and{' '}
          <strong>wines</strong> that impressed us and remained in our hearts.
        </p>
        <p>
          All the producers we work with are <strong>artisans</strong> who dedicate themselves with
          respect to limited extensions of vineyards: many <strong>agricultural organisms</strong>{' '}
          that live and exist in harmony with the surrounding ecosystem. The common thread:{' '}
          <strong>people</strong>.
        </p>
        <p>
          We consider all the producers we work with as friends, and we try to spend more time with
          them: <strong>in the vineyard, in the cellar, at the table, while traveling</strong>.
        </p>
        <p>
          That&apos;s why when asked to define in one word what we do, the answer is agents: our job
          is to{' '}
          <strong>
            bring to you a bit of the philosophy, way of life, and territories of our producers
          </strong>
          .
        </p>
      </div>
    ) : (
      <div className="pdf-about-page-description-text">
        <p>
          La nostra distribuzione prende vita alla fine del 2014, alimentata dalla{' '}
          <strong>curiosità</strong> di scoprire <strong>vini,</strong> territori e{' '}
          <strong>persone</strong> che hanno saputo ispirarci e sorprenderci.
        </p>
        <p>
          Collaboriamo con <strong>piccoli produttori artigiani</strong>, custodi appassionati di{' '}
          <strong>estensioni limitate di</strong> vigna che coltivano con rispetto, in armonia con
          l'ambiente che li circonda. I loro vini sono{' '}
          <strong>espressioni autentiche di un ecosistema vivo,</strong> ricco di storie e
          tradizioni.
        </p>
        <p>
          Con il tempo, <strong>la nostra rete di produttori si è ampliata,</strong> attraversando
          confini e abbracciando realtà vitivinicole <strong>da tutta Europa.</strong> Ogni incontro
          ha segnato una <strong>tappa del nostro percorso,</strong> territori e visioni che si
          intrecciano in un <strong>racconto condiviso.</strong>
        </p>
        <p>
          Al centro di tutto ci sono <strong>le persone.</strong> I produttori con cui lavoriamo
          sono <strong>collaboratori e amici</strong> con cui condividiamo una{' '}
          <strong>visione</strong> e un legame speciale. Trascorriamo con loro tempo prezioso in
          vigna, in cantina, a tavola e in viaggio, vivendo <strong>esperienze</strong> che
          aggiungono significato al nostro lavoro.
        </p>
        <p>
          Ogni bottiglia è <strong>un capitolo di questa storia,</strong> che racconta luoghi, mani
          e tradizioni.
        </p>
      </div>
    );

  const modifyDiscountValue = useMemo(
    () =>
      voca.replace(
        fPercent((discount ?? 0) < 0 ? (discount ?? 0) * -1 : (discount ?? 0)),
        '.',
        ',',
      ),
    [discount],
  );

  const page2Info2WithDiscountHtml = (info: string, subinfo: string, dynamicInfo: any) => (
    <>
      <span className="pdf-p">
        {`${t(info)} `}
        <strong className="pdf-p-bold">{` ${modifyDiscountValue} ${t(dynamicInfo.info3)}`}</strong>
      </span>
      <span className="pdf-p">{t(dynamicInfo.info4)}</span>
      <span className="pdf-p">{t(subinfo)}</span>
      <br />
      <br />
    </>
  );

  const formattedData = useMemo(() => {
    const pdfApiData = productApiData as
      | {
          token?: string;
          products?: IProductItem[];
          data?: IProductItem[];
        }
      | null
      | undefined;

    let productsSource: IProductItem[] = [];
    if (Array.isArray(pdfApiData?.products)) {
      productsSource = pdfApiData!.products!;
    } else if (Array.isArray(pdfApiData?.data)) {
      productsSource = pdfApiData!.data!;
    }

    if (!Array.isArray(productsSource) || productsSource.length === 0) return [];

    const grouped: {
      country: string;
      producers: {
        producerName: string;
        products: (IProductItem & { availabilityClass: string })[];
        country: string;
        producerTerritory?: string;
      }[];
    }[] = [];

    const altroGrouped: {
      country: string;
      producers: {
        producerName: string;
        products: (IProductItem & { availabilityClass: string })[];
        country: string;
        producerTerritory?: string;
      }[];
    }[] = [];

    productsSource.forEach((product: IProductItem) => {
      // Exclude products where shown is explicitly false
      if (product.shown === false) return;

      const country = product.producer_country;
      const producerName = product.producer_name;
      const producerTerritory = product.producer_territory || product.producer_region;
      const isWine = !NON_WINE_TYPE_keys.includes(product.wine_type);

      const targetGrouped = isWine ? grouped : altroGrouped;

      // Find or create country group
      let countryGroup = targetGrouped.find((g) => g.country === country);
      if (!countryGroup) {
        countryGroup = {
          country,
          producers: [],
        };
        targetGrouped.push(countryGroup);
      }

      // Find or create producer group within country
      let producerGroup = countryGroup.producers.find((p) => p.producerName === producerName);

      if (!producerGroup) {
        producerGroup = {
          producerName,
          country,
          producerTerritory,
          products: [],
        };
        countryGroup.producers.push(producerGroup);
      }

      const enrichedProduct: IProductItem & { availabilityClass: string } = {
        ...(product as unknown as IProductItem),
        availabilityClass: getAvailabilityClass(product.available_quantity),
        price: fCurrencyAfterSymbol(product.price) || '---',
      };

      producerGroup.products.push(enrichedProduct);
    });

    const wineData = grouped.sort((a, b) => {
      if (a.country.toLowerCase() === 'italia') return -1;
      if (b.country.toLowerCase() === 'italia') return 1;
      return a.country.localeCompare(b.country);
    });

    const altroData = altroGrouped.sort((a, b) => {
      if (a.country.toLowerCase() === 'italia') return -1;
      if (b.country.toLowerCase() === 'italia') return 1;
      return a.country.localeCompare(b.country);
    });

    return { wineData, altroData };
  }, [productApiData]);

  // Estimate index country block height based on producer count
  const estimateIndexCountryHeight = (producerCount: number): number => {
    const rows = Math.ceil(producerCount / 2);
    return INDEX_COUNTRY_HEADER_HEIGHT + rows * INDEX_PRODUCER_ROW_HEIGHT + INDEX_COUNTRY_MARGIN;
  };

  // Estimate product producer block height
  const estimateProducerBlockHeight = (productCount: number): number =>
    PRODUCT_PRODUCER_HEADER_HEIGHT +
    PRODUCT_TABLE_HEADER_HEIGHT +
    productCount * PRODUCT_ROW_HEIGHT +
    PRODUCT_PRODUCER_MARGIN;

  // Render a single index country block JSX
  const renderIndexCountryBlock = (
    countryGroup: any,
    countryIndex: number,
    isAltroSection: boolean,
    showHeader: boolean,
  ) => {
    const producers = countryGroup.producers || [];
    const half = Math.ceil(producers.length / 2);
    const firstColumnProducers = producers.slice(0, half);
    const secondColumnProducers = producers.slice(half);

    return (
      <div
        key={`${countryGroup.country || countryIndex}-${showHeader ? 'h' : 'c'}`}
        className="pdf-index-country-section"
      >
        {showHeader && (
          <a
            href={
              isAltroSection
                ? `#altro-prod-country-${slugify(countryGroup.country)}`
                : `#main-prod-country-${slugify(countryGroup.country)}`
            }
            title={voca.upperCase(countryGroup.country)}
            className="pdf-index-country-link"
          >
            <div className="pdf-index-country-name">
              <span>{voca.upperCase(countryGroup.country)}</span>
            </div>
          </a>
        )}

        <div className="pdf-index-two-column-row">
          <div className="pdf-index-producer-column">
            {firstColumnProducers.map((producerItem: any) => (
              <div
                key={producerItem.producer_slug || producerItem.producerName}
                className="pdf-index-producer-row"
              >
                <div className="pdf-index-producer-name-container">
                  <a
                    className="pdf-index-producer-link"
                    href={
                      isAltroSection
                        ? `#altro-prod-${slugify(producerItem.producerName)}`
                        : `#main-prod-${slugify(producerItem.producerName)}`
                    }
                    title={voca.upperCase(producerItem.producerName)}
                  >
                    {producerItem.producerName}
                  </a>
                  <span className="pdf-index-producer-origin">
                    {producerItem.producerTerritory}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="pdf-index-producer-column">
            {secondColumnProducers.map((producerItem: any) => (
              <div
                key={producerItem.producer_slug || producerItem.producerName}
                className="pdf-index-producer-row"
              >
                <div className="pdf-index-producer-name-container">
                  <a
                    className="pdf-index-producer-link"
                    href={
                      isAltroSection
                        ? `#altro-prod-${slugify(producerItem.producerName)}`
                        : `#main-prod-${slugify(producerItem.producerName)}`
                    }
                    title={voca.upperCase(producerItem.producerName)}
                  >
                    {producerItem.producerName}
                  </a>
                  <span className="pdf-index-producer-origin">
                    {producerItem.producerTerritory}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderIndexPages = () => {
    const hasWineData = formattedData && 'wineData' in formattedData;
    const hasAltroData = formattedData && 'altroData' in formattedData;
    const pages: React.JSX.Element[] = [];
    let currentBlocks: React.JSX.Element[] = [];
    let remainingHeight = PAGE_USABLE_HEIGHT;
    let pageKey = 0;

    const flushPage = () => {
      if (currentBlocks.length === 0) return;
      const snapshot = [...currentBlocks];
      pageKey += 1;
      pages.push(
        <React.Fragment key={`index-page-${pageKey}`}>
          {renderPage(
            <div className="pdf-index-page-container">
              <div className="pdf-index-content-area">{snapshot}</div>
            </div>,
            false,
            true,
          )}
        </React.Fragment>,
      );
      currentBlocks = [];
      remainingHeight = PAGE_USABLE_HEIGHT;
    };

    // Add INDICE title + Vini subtitle on first page
    const titleBlock = (
      <React.Fragment key="index-title">
        <h1 className="pdf-index-main-title">{voca.upperCase(t('indice'))}</h1>
        <h2 className="pdf-index-sub-title">{voca.upperCase('vini')}</h2>
      </React.Fragment>
    );
    currentBlocks.push(titleBlock);
    remainingHeight -= INDEX_MAIN_TITLE_HEIGHT + INDEX_SUB_TITLE_HEIGHT;

    // Wine countries
    if (hasWineData) {
      formattedData.wineData.forEach((countryGroup: any, i: number) => {
        const producers = countryGroup.producers || [];
        const blockHeight = estimateIndexCountryHeight(producers.length);

        if (blockHeight > remainingHeight && currentBlocks.length > 0) {
          flushPage();
        }

        currentBlocks.push(renderIndexCountryBlock(countryGroup, i, false, true));
        remainingHeight -= blockHeight;
      });
    }

    // Altro subtitle
    if (hasAltroData) {
      const altroTitleHeight = INDEX_SUB_TITLE_HEIGHT + 32; // 32px margin-top
      const firstCountryProducers = formattedData.altroData[0]?.producers || [];
      const firstCountryHeight = estimateIndexCountryHeight(firstCountryProducers.length);

      // Ensure title + first country stay together on the same page
      if (altroTitleHeight + firstCountryHeight > remainingHeight && currentBlocks.length > 0) {
        flushPage();
      }

      currentBlocks.push(
        <h2 key="altro-title" className="pdf-index-sub-title pdf-index-altro-title">
          Altre fermentazioni, birre, sidri, distllati, condimenti, etc
        </h2>,
      );
      remainingHeight -= altroTitleHeight;

      // Altro countries
      formattedData.altroData.forEach((countryGroup: any, i: number) => {
        const producers = countryGroup.producers || [];
        const blockHeight = estimateIndexCountryHeight(producers.length);

        if (blockHeight > remainingHeight && currentBlocks.length > 0) {
          flushPage();
        }

        currentBlocks.push(renderIndexCountryBlock(countryGroup, i, true, true));
        remainingHeight -= blockHeight;
      });
    }

    // Flush remaining
    flushPage();

    return <>{pages}</>;
  };

  // Render a single producer block JSX (used by product listing pagination)
  const renderProducerBlockJsx = (
    producerItem: any,
    countryIndex: number,
    producerIndex: number,
    prefix: string,
    isAltro: boolean,
  ) => (
    <div
      key={`${prefix}-prod-${countryIndex}-${producerIndex}`}
      className="pdf-producer-block"
      data-testid="product-page-container"
    >
      <div className="pdf-producer-section">
        <div className={`pdf-producer-block-header ${isAltro ? '' : 'pdf-mb32'}`}>
          <h4
            className="pdf-producer-header"
            id={`${prefix}-prod-${slugify(producerItem.producerName)}`}
          >
            {producerItem.producerName}
          </h4>
          <span className="pdf-producer-header-origin">{producerItem.producerTerritory}</span>
        </div>
        <div className="pdf-product-table-header">
          <div className="pdf-product-cell-vino-header">
            <span className="pdf-product-table-header-text">{t('wine')}</span>
          </div>
          <div className="pdf-product-cell-anno-header">
            <span className="pdf-product-table-header-text">{t('year')}</span>
          </div>
          <div className="pdf-product-cell-uve-header">
            <span className="pdf-product-table-header-text">{t('grapes')}</span>
          </div>
          <div className="pdf-product-cell-disponibilita-header">
            <span className="pdf-product-table-header-text">{t('availability')}</span>
          </div>
          <div className="pdf-product-cell-promo-header">
            <span className="pdf-product-table-header-text">{t('promo')}</span>
          </div>
          <div className="pdf-product-cell-prezzo-header">
            <span className="pdf-product-table-header-text">{t('price')}</span>
          </div>
        </div>

        <div className="pdf-single-product-column">
          {producerItem.products.map((product: any, productIndex: number) => {
            const isEven = productIndex % 2 === 0;
            // eslint-disable-next-line no-nested-ternary
            const rowClass = isEven
              ? isAltro
                ? 'pdf-product-row-even-alt'
                : 'pdf-product-row-even'
              : isAltro
                ? 'pdf-product-row-odd-alt'
                : 'pdf-product-row-odd';
            return (
              <div key={product.sku} className={`pdf-product-row ${rowClass}`}>
                <div className="pdf-product-cell-vino">
                  <NameCell label={product.name} info={product.wine_type} />
                </div>
                <div className="pdf-product-cell-anno">
                  <span className="pdf-product-table-text">{product.vintage ?? '---'}</span>
                </div>
                <div className="pdf-product-cell-uve">
                  <span className={isAltro ? 'pdf-product-table-text' : 'pdf-product-grape-type'}>
                    {(() => {
                      try {
                        const parsed = JSON.parse(product.grape_type as string);
                        return Array.isArray(parsed) ? parsed.join(', ') : '---';
                      } catch {
                        return product.grape_type;
                      }
                    })()}
                  </span>
                </div>
                <div className="pdf-product-cell-disponibilita">
                  <div className="pdf-availability-bar-outer">
                    <div className={`pdf-availability-bar-inner ${product.availabilityClass}`} />
                  </div>
                </div>
                <div className="pdf-product-cell-promo">
                  {(product.promotion_discount || discount) && (
                    <span className="pdf-product-promo-badge">
                      {fPercent(product.promotion_discount || discount)}
                    </span>
                  )}
                </div>
                <div className="pdf-product-cell-prezzo">
                  {product.promotion_discount || discount ? (
                    <div className="pdf-product-price-section">
                      <span className="pdf-product-price-discounted">
                        {fCurrencyAfterSymbol(product.adjusted_price)}
                      </span>
                      <span className="pdf-product-price-without-discount">{product.price}</span>
                    </div>
                  ) : (
                    <span className="pdf-product-price">{product.price}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Paginate product listings: pack multiple producers per page, never split a producer
  const renderProductListingPages = (data: any[], prefix: string, isAltro: boolean) => {
    const pages: React.JSX.Element[] = [];
    let currentPageBlocks: React.JSX.Element[] = [];
    let remainingHeight = PAGE_USABLE_HEIGHT;
    let productPageKey = 0;

    const flushPage = () => {
      if (currentPageBlocks.length === 0) return;
      const blocksToRender = [...currentPageBlocks]; // snapshot
      productPageKey += 1;
      pages.push(
        <React.Fragment key={`${prefix}-page-${productPageKey}`}>
          {renderPage(
            <div className="pdf-product-page-container product-page-container">
              {blocksToRender}
            </div>,
            false,
            true,
          )}
        </React.Fragment>,
      );
      currentPageBlocks = [];
      remainingHeight = PAGE_USABLE_HEIGHT;
    };

    // Add section title for Altro listings
    if (isAltro) {
      const sectionTitleHeight = PRODUCT_COUNTRY_HEADER_HEIGHT;
      currentPageBlocks.push(
        <div key={`${prefix}-section-title`} className="pdf-producer-page-top-header">
          <span
            className="pdf-product-page-header-left"
            style={{ fontSize: '26px', letterSpacing: '2px' }}
          >
            Altre fermentazioni, birre, sidri, distillati, condimenti, etc
          </span>
        </div>,
      );
      remainingHeight -= sectionTitleHeight;
    }

    data.forEach((countryGroup: any, countryIndex: number) => {
      const producers = countryGroup.producers || [];

      producers.forEach((producerItem: any, producerIndex: number) => {
        const productCount = producerItem.products?.length || 0;
        // Include country header height for first producer of each country
        const needsCountryHeader = producerIndex === 0;
        const countryHeaderHeight = needsCountryHeader ? PRODUCT_COUNTRY_HEADER_HEIGHT : 0;
        const blockHeight = countryHeaderHeight + estimateProducerBlockHeight(productCount);

        // If this producer doesn't fit, flush current page
        if (blockHeight > remainingHeight && currentPageBlocks.length > 0) {
          flushPage();
        }

        // Add country header if first producer of country
        if (needsCountryHeader) {
          currentPageBlocks.push(
            <div
              key={`${prefix}-country-header-${countryIndex}`}
              className="pdf-producer-page-top-header"
              id={`${prefix}-prod-country-${slugify(countryGroup.country)}`}
            >
              <span className="pdf-product-page-header-left">
                {voca.upperCase(countryGroup.country)}
              </span>
            </div>,
          );
          remainingHeight -= countryHeaderHeight;
        }

        // Add producer block
        currentPageBlocks.push(
          renderProducerBlockJsx(producerItem, countryIndex, producerIndex, prefix, isAltro),
        );
        remainingHeight -= estimateProducerBlockHeight(productCount);
      });
    });

    // Flush remaining
    flushPage();

    return <>{pages}</>;
  };

  let currentPageCounter = 0;
  const renderPage = (
    content: React.ReactNode,
    isColorPage: boolean = false,
    hasFooter: boolean = true,
    skipPageBreak: boolean = false,
  ) => {
    currentPageCounter += 1;

    const pageClass = [
      isColorPage ? 'pdf-color-page' : 'pdf-page',
      !skipPageBreak ? 'pdf-page-break' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={pageClass}>
        {content}
        {hasFooter && (
          <Footer
            useWhiteLogo={isColorPage}
            footerSeparatorColor={isColorPage ? '#ffffff' : '#DFE3E8'}
            isCustomerPagePresent={Boolean(parsedData.client)}
            currentPage={currentPageCounter}
          />
        )}
      </div>
    );
  };

  return (
    <div ref={pagedContainerRef} className="pdf-document-container" id="paged-preview-content">
      {/* 1. Cover Page */}
      {renderPage(
        <div className="pdf-cover-page-container">
          <div className="pdf-cover-top-logo-container">
            <img src={NativesLogoOriginal} className="pdf-cover-top-logo" alt={t('natives_logo')} />
          </div>

          <div className="pdf-cover-content-container">
            <h1 className="pdf-cover-h1">
              {voca.upperCase(currentLang.value === 'it' ? t('list') : t('prices'))}
              <br />
              {voca.upperCase(currentLang.value === 'it' ? t('prices') : t('list'))}
            </h1>
            {parsedData.client && (
              <p className="pdf-cover-sub-title">
                {`${t('reserved_for')}: `}
                <br />
                <span className="pdf-cover-h3">{voca.upperCase(customerName)}</span>
              </p>
            )}
            <p className="pdf-cover-sub-title">
              {`${t('product_pdf_footer_text')}`}
              <p className="pdf-cover-info-text">
                <span className="pdf-cover-info">{todaysDate}</span>
              </p>
            </p>
            {latestDownloadLink && (
              <p className="pdf-cover-pdf-download-container">
                <p className="pdf-cover-sub-title">{`${t('product_pdf_download_text_part_1')}`}</p>
                <p className="pdf-cover-sub-title">{`${t('product_pdf_download_text_part_2')}`}</p>
                <p className="pdf-cover-sub-title">
                  <a
                    href={latestDownloadLink}
                    className="pdf-cover-download-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('click_here')}
                  </a>
                  {`${t('product_pdf_download_text_part_3')}`}
                </p>
              </p>
            )}
          </div>
        </div>,
        true,
        false,
        true, // skipPageBreak = true for first page
      )}

      {/* 2. About Us Page */}
      {renderPage(
        <div className="pdf-about-page-main-content-container">
          <img src={NativesLogo} className="pdf-about-page-logo" alt={t('natives_logo')} />
          <h1 className="pdf-about-page-title">
            {voca.upperCase(t('product_pdf_about_pg_title'))}
          </h1>
          {abtPageDesc}
          <img src={fair} className="pdf-about-page-img" alt={t('fair')} />
        </div>,
        false,
        true,
      )}

      {/* 3. General Rules Page */}
      {renderPage(
        <div className="pdf-general-rules-content-container">
          <h1 className="pdf-general-rules-title">{`${t('product_pdf_gen_rules_title')}`}</h1>

          <SubLabel label={t('product_pdf_gen_rules_2_pt_1_label')} logo={WalletLogo} />
          <div className="pdf-mb24">
            {PAGE_2_INFO_1.map((dtl, index) => (
              <LabelAndInfo
                key={index}
                label={dtl.label}
                info={!dtl.dynamic ? dtl.info : paymentDuration}
              />
            ))}
          </div>

          <SubLabel label={t('product_pdf_gen_rules_2_subtitle')} logo={DiscountsLogo} />
          <div className="pdf-mb20">
            {!discount &&
              PAGE_2_INFO_2.map((dtl, index) => (
                <LabelAndInfo key={index} label={dtl.label} info={dtl.info} />
              ))}
            {discount &&
              PAGE_2_INFO_2_WITH_DISCOUNT.map((dtl, index) => (
                <div key={index} className="pdf-label-and-info-container">
                  {dtl.label && <p className="pdf-label-and-info-label">{t(dtl.label)}</p>}
                  {dtl.dynamic
                    ? dtl.info &&
                      page2Info2WithDiscountHtml(dtl.info, dtl.subInfo ?? '', dtl.dynamicInfo)
                    : dtl.info && <p className="pdf-label-and-info-info">{t(dtl.info)}</p>}
                </div>
              ))}
          </div>

          <SubLabel label={t('product_pdf_gen_rules_3_subtitle')} logo={DeliveryLogo} />
          <div className="pdf-mb20">
            {page2Info3.map((dtl, index) => (
              <LabelAndInfo key={index} label={dtl.label} info={dtl.info} />
            ))}
          </div>

          <SubLabel label={t('product_pdf_gen_rules_4_subtitle')} logo={PackageLogo} />
          <div className="pdf-mb0">
            {PAGE_2_INFO_4.map((dtl, index) => (
              <LabelAndInfo key={index} label={dtl.label} info={dtl.info} />
            ))}
          </div>
        </div>,
        false,
        true,
      )}

      {/* 4. News Page — only rendered when a popup is active and within its set dates */}
      {validActiveOverlay &&
        validActiveOverlay.content &&
        renderPage(
          <div className="pdf-news-page-main-content-container">
            <span className="pdf-news-title">
              {voca.upperCase(t('cosa_ce_di'))}
              <br />
              {voca.upperCase(t('nuovo'))}
            </span>

            <div className="pdf-news-content-section-background">
              <div className="pdf-news-content-text-wrapper">
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: validActiveOverlay.content.replace(/<p><br><\/p>/g, ''),
                  }}
                />
              </div>

              <img src={planeIcon} className="pdf-plane-icon" alt="Plane Icon" />
            </div>
          </div>,
          false,
          true,
        )}

      {renderIndexPages()}

      {/* 6. Main Product Listing Pages - Vini */}
      {formattedData &&
        'wineData' in formattedData &&
        renderProductListingPages(formattedData.wineData, 'main', false)}

      {/* 7. Altro Product Listing Pages */}
      {formattedData &&
        'altroData' in formattedData &&
        renderProductListingPages(formattedData.altroData, 'altro', true)}
    </div>
  );
}
